import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router';

import { DecoOrnament } from '../../atoms/DecoOrnament';
import { DecoSeparator } from '../../atoms/DecoSeparator';
import { NavIcon, type NavIconName } from '../../atoms/NavIcon';
import type { TestIdProps } from '../../testing';
import styles from './MobileMenu.module.css';

export interface MobileMenuLink {
  readonly label: string;
  readonly href: string;
}

export interface MobileMenuProps extends TestIdProps {
  links: readonly MobileMenuLink[];
  enquiryCount: number;
  enquiryHref?: string | undefined;
  enquiryLabel?: string | undefined;
  className?: string | undefined;
}

const ICON_BY_HREF: Record<string, NavIconName> = {
  '/': 'home',
  '/about': 'about',
  '/shop': 'shop',
  '/articles': 'articles',
  '/faq': 'faq',
  '/contact': 'contact',
};

function iconForHref(href: string): NavIconName {
  return ICON_BY_HREF[href] ?? 'articles';
}

/**
 * `closing` keeps the overlay mounted while its exit animation plays; it
 * unmounts on `closed`. The closed → open → closing → closed cycle is what
 * lets the menu animate out instead of being removed abruptly by React.
 */
type MenuState = 'closed' | 'open' | 'closing';

/** Safety net: unmount the overlay even if `animationend` never fires. */
const EXIT_FALLBACK_MS = 600;

/** Everything keyboard focus can land on inside the dialog (the panel itself is tabindex=-1). */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Phone/tablet navigation: a burger toggle that opens a full-screen overlay. */
export function MobileMenu({
  links,
  enquiryCount,
  enquiryHref = '/cart',
  enquiryLabel = 'Enquiry',
  className,
  'data-testid': testId = 'mobile-menu',
}: MobileMenuProps) {
  const [state, setState] = useState<MenuState>('closed');
  const open = state === 'open';
  const rendered = state !== 'closed';
  const { pathname } = useLocation();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const scrimRef = useRef<HTMLButtonElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // Begin the exit: animate out unless reduced-motion is on, in which case no
  // animationend would fire so we drop straight to closed.
  const close = useCallback(() => {
    setState((current) =>
      current === 'open' ? (prefersReducedMotion() ? 'closed' : 'closing') : current,
    );
  }, []);

  // Finish the exit: the overlay leaves the DOM only after the animation ends.
  const finishClose = useCallback(() => {
    setState((current) => (current === 'closing' ? 'closed' : current));
  }, []);

  // Close on route change so navigating from inside the overlay dismisses it.
  const lastPath = useRef(pathname);
  useEffect(() => {
    if (lastPath.current !== pathname) {
      lastPath.current = pathname;
      close();
    }
  }, [pathname, close]);

  // Belt-and-braces: if animationend is missed (or animations are disabled),
  // still unmount after the exit window.
  useEffect(() => {
    if (state !== 'closing') return undefined;
    const timer = window.setTimeout(finishClose, EXIT_FALLBACK_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [state, finishClose]);

  // While the overlay is on screen (open or animating out): lock body scroll.
  // Once it is gone, return focus to the trigger — unless something else
  // claimed focus meanwhile (a route change focuses the new page's main
  // landmark; the overlay is inert while closing, so focus can't be in it).
  useEffect(() => {
    if (!rendered) return;
    const { body } = document;
    const previousOverflow = body.style.overflow;
    const trigger = triggerRef.current;
    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
      const active = document.activeElement;
      if (active === null || active === body) trigger?.focus();
    };
  }, [rendered]);

  // While open: make everything behind the overlay inert (unreachable by
  // focus, screen-reader exploration and pointer alike), move focus into the
  // dialog, close on Escape, and wrap Tab. The overlay is portalled to <body>,
  // so its siblings are exactly "the rest of the page". Inert is lifted as soon
  // as closing starts so a navigation triggered from the menu can focus the new
  // page (link clicks close the menu in the same update as the route change).
  useEffect(() => {
    if (!open) return;
    const overlay: readonly (Element | null)[] = [scrimRef.current, dialogRef.current];
    const background = Array.from(document.body.children).filter(
      (node): node is HTMLElement =>
        node instanceof HTMLElement && !overlay.includes(node) && !node.inert,
    );
    for (const node of background) node.inert = true;
    dialogRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (dialog === null) return;
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((node) => node.getClientRects().length > 0);
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      for (const node of background) node.inert = false;
    };
  }, [open, close]);

  const classes = [styles['root'], className].filter(Boolean).join(' ');

  return (
    <div className={classes} data-testid={testId}>
      <button
        ref={triggerRef}
        type="button"
        className={styles['trigger']}
        aria-label="Open menu"
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid={`${testId}-trigger`}
        onClick={() => {
          setState('open');
        }}
      >
        <span className={styles['burger']} aria-hidden="true">
          <span className={styles['burgerLine']} />
          <span className={styles['burgerLine']} />
          <span className={styles['burgerLine']} />
        </span>
      </button>

      {rendered &&
        typeof document !== 'undefined' &&
        createPortal(
          <>
            {/* Pointer-only dismiss target: the header close button covers keyboard and AT. */}
            <button
              ref={scrimRef}
              type="button"
              className={styles['scrim']}
              data-state={state}
              aria-hidden="true"
              tabIndex={-1}
              data-testid={`${testId}-scrim`}
              onClick={close}
              inert={state === 'closing'}
            />
            <div
              ref={dialogRef}
              className={styles['panel']}
              data-state={state}
              role="dialog"
              aria-modal="true"
              aria-label="Site menu"
              tabIndex={-1}
              data-testid={`${testId}-dialog`}
              inert={state === 'closing'}
              onAnimationEnd={(event) => {
                if (event.target === event.currentTarget) finishClose();
              }}
            >
              <div className={styles['overlayTop']}>
                <Link
                  to="/"
                  className={styles['brand']}
                  aria-label="Basement Pickups — home"
                  onClick={close}
                  data-testid={`${testId}-brand`}
                >
                  <img
                    src="/assets/logo/BP_Gold_horizont.svg"
                    alt=""
                    width={273}
                    height={100}
                    className={styles['logo']}
                  />
                </Link>
                <button
                  type="button"
                  className={styles['close']}
                  aria-label="Close menu"
                  onClick={close}
                  data-testid={`${testId}-close`}
                >
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="square"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M5 5 L19 19" vectorEffect="non-scaling-stroke" />
                    <path d="M19 5 L5 19" vectorEffect="non-scaling-stroke" />
                  </svg>
                </button>
              </div>

              <DecoSeparator variant="crest" className={styles['topDivider']} />

              <nav aria-label="Primary" className={styles['nav']}>
                <ul className={styles['list']} role="list">
                  {links.map((link) => (
                    <li key={link.href} className={styles['item']}>
                      <NavLink
                        to={link.href}
                        end={link.href === '/'}
                        onClick={close}
                        className={({ isActive }) =>
                          [styles['link'], isActive ? styles['linkActive'] : undefined]
                            .filter(Boolean)
                            .join(' ')
                        }
                        data-testid={`${testId}-link-${link.href === '/' ? 'home' : link.href.slice(1)}`}
                      >
                        <NavIcon
                          name={iconForHref(link.href)}
                          size={22}
                          className={styles['linkIcon']}
                        />
                        <span className={styles['linkLabel']}>{link.label}</span>
                        <DecoOrnament
                          variant="diamond"
                          filled
                          size={8}
                          className={styles['linkDiamond']}
                        />
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </nav>

              <DecoSeparator variant="medallion" className={styles['divider']} />

              <Link
                to={enquiryHref}
                className={styles['enquiry']}
                onClick={close}
                data-testid={`${testId}-enquiry`}
              >
                <span className={styles['enquiryGlyph']}>
                  <NavIcon name="cart" size={52} />
                  {enquiryCount > 0 && (
                    <span
                      className={styles['badge']}
                      aria-hidden="true"
                      data-testid={`${testId}-badge`}
                    >
                      {enquiryCount}
                    </span>
                  )}
                </span>
                <span className={styles['enquiryText']}>
                  <span className={styles['enquiryLabel']}>
                    {enquiryLabel}
                    {enquiryCount > 0 && (
                      <span
                        className={styles['srOnly']}
                      >{`, ${String(enquiryCount)} ${enquiryCount === 1 ? 'item' : 'items'}`}</span>
                    )}
                  </span>
                  <span className={styles['enquiryHint']}>View your enquiry list</span>
                </span>
              </Link>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
